import {
  Brain,
  Camera,
  ClipboardCheck,
  FileText,
  Globe,
  MailCheck,
  PlayCircle,
  ShieldCheck,
  Smartphone,
  Sparkles,
  ThumbsUp,
  UserPlus,
} from "lucide-react";

import scenarioImage from "assets/muggleTestV0/public/ai-scenario-generation-interface.jpg";
import resultsDashboardImage from "assets/muggleTestV0/public/test-results-dashboard.jpg";
import urlInputImage from "assets/muggleTestV0/public/url-input-interface.jpg";
import evidencePreviewImage from "assets/muggleTestV0/public/web-testing-screenshot-with-error.jpg";
import {
  ExampleUseCaseIconEnum,
  HeroWorkflowPersonaTabEnum,
  PricingPlanCtaActionEnum,
  PricingPlanPriceDisplayEnum,
} from "types/landing/landing.types";
import type {
  AgentCapability,
  ClosingCta,
  ExampleCard,
  FaqItem,
  FooterLink,
  HeroWorkflowFlow,
  HowItWorksStep,
  NavItem,
  PreviewProvider,
  PricingPlan,
  SchemaMarkup,
  WhyChooseLadderBenefit,
  WhyChooseLadderOutcomeCard,
} from "types/landing/landing.types";
import { SubscriptionPlanEnum } from "types/subscription/SubscriptionPlanEnum";

export const DASHBOARD_RETURN_TO = "/muggleTestV0/dashboard";
export const EVIDENCE_PREVIEW_IMAGE = evidencePreviewImage;
export const SAMPLE_REPORT_IMAGE = "/muggleTestV0/public/images/Full%20Test%20Report.png";

/** Intrinsic size of `Full Test Report.png` (layout reserve / aspect ratio in modals). */
export const SAMPLE_REPORT_IMAGE_WIDTH_PX = 2522;
export const SAMPLE_REPORT_IMAGE_HEIGHT_PX = 6516;

/** Alt text for the full sample report screenshot in dialogs and lightboxes. */
export const SAMPLE_REPORT_IMAGE_ALT =
  "Muggle Test sample HTML report: project overview, AI insights, bugs by severity, UX issues, and coverage";

/** Hero-side preview image in the landing overview section (focused workflow detail crop). */
export const HERO_TEST_REPORT_PREVIEW_IMAGE = "/muggleTestV0/public/images/overview-hero-detail.png";

/** Alt text for the hero preview image. */
export const HERO_TEST_REPORT_PREVIEW_ALT =
  "Workflow detail showing development and product release steps in Muggle Test";

/** Primary CTA label used in the hero section and pricing cards. */
export const HERO_CTA_PRIMARY = "Start Free Testing";

export const NAV_ITEMS: NavItem[] = [
  { label: "Overview", href: "#overview" },
  { label: "How it works", href: "#how-it-works" },
  { label: "Use cases", href: "#examples" },
  { label: "Why choose Muggle Test?", href: "#why-choose-muggle-test" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

/** Featured benefit card in the ladder (matches `title` on one ladder item). */
export const WHY_CHOOSE_MUGGLE_TEST_FEATURED_TITLE = "Results in minutes";

/** Section subtitle under the H2. */
export const WHY_CHOOSE_MUGGLE_TEST_SECTION_DESCRIPTION =
  "Structured by what hurts release quality most—then what you get on every run.";

/** Label above the first ladder tier (pressure → relief). */
export const WHY_CHOOSE_LADDER_TIER1_HEADING = "What teams optimize for";

/** Label above the second ladder tier (outcomes). */
export const WHY_CHOOSE_LADDER_TIER2_HEADING = "What each run produces";

/** Label above the third ladder tier (real-world agent capabilities). */
export const WHY_CHOOSE_LADDER_TIER3_HEADING = "Beyond click-and-type";

/**
 * Real-world agent capabilities surfaced as hero chips and as the tier-3
 * grid in the why-choose ladder. Order is tuned for the hero strip:
 * the four "external friction" capabilities first (signup, email, SMS,
 * captcha), then the two "self-improvement" capabilities last.
 */
export const AGENT_CAPABILITIES: AgentCapability[] = [
  {
    title: "Signs up like a real user",
    shortLabel: "Auto signup",
    description:
      "Agents create disposable test identities — no manual setup, no shared logins, no flaky test fixtures.",
    icon: UserPlus,
  },
  {
    title: "Reads verification emails",
    shortLabel: "Email verify",
    description:
      "Picks up confirmation links and codes from the inbox automatically. Full signup flows run end-to-end without you in the loop.",
    icon: MailCheck,
  },
  {
    title: "Handles SMS codes",
    shortLabel: "SMS codes",
    description:
      "Receives and enters one-time codes during signup, login, and 2FA. Phone-gated flows test like everything else.",
    icon: Smartphone,
  },
  {
    title: "Solves captchas",
    shortLabel: "Captchas",
    description:
      "Passes reCAPTCHA, image puzzles, and similar challenges so bot defenses don't block real test runs.",
    icon: ShieldCheck,
  },
  {
    title: "Learns from past runs",
    shortLabel: "Memory",
    description:
      "Remembers what worked and what didn't on your app. Every run is faster and more reliable than the last.",
    icon: Brain,
  },
  {
    title: "Improves from your feedback",
    shortLabel: "Feedback loop",
    description:
      "Flag a bad step once and the agent adjusts. Your corrections train it on your app's specific quirks.",
    icon: ThumbsUp,
  },
];

/**
 * Ladder tier 1: pressure chip → relief card, ordered by painfulness.
 */
export const WHY_CHOOSE_LADDER_BENEFITS: WhyChooseLadderBenefit[] = [
  {
    pressureLabel: "Release speed",
    title: "Results in minutes",
    description:
      "Paste a preview URL, run your picks, and get a full report while the build is still fresh—not after a slow manual E2E acceptance pass.",
  },
  {
    pressureLabel: "Maintenance cost",
    title: "Zero scripting",
    description:
      "Choose the journeys that matter; agents exercise your app like real users—no suites to write or babysit.",
  },
  {
    pressureLabel: "Engineering time",
    title: "Built for lean teams",
    description:
      "Release-ready signal with the people you already have—without standing up a dedicated testing org or losing nights before ship.",
  },
];

/**
 * Structured data used by the hero React Flow visualization.
 */
export const HERO_WORKFLOW_FLOW: HeroWorkflowFlow = {
  title: "Ship with confidence",
  subtitle: "Role-based quality paths",
  description: "See exactly how each role catches risk before release.",
  personaFlows: {
    [HeroWorkflowPersonaTabEnum.Developers]: {
      label: "Developers",
      description: "Code, test, and build up a reliable test plan.",
      steps: [
        {
          stepNumber: "1",
          title: "Build a feature",
          description: "Build the feature locally and verify baseline behavior.",
        },
        {
          stepNumber: "2",
          title: "Muggle Test it",
          description: "Run Muggle test locally to validate key user journeys.",
          highlight: true,
        },
        {
          stepNumber: "3",
          title: "Fix issues util works",
          description: "Keep fixing issues from feedbacks until checks pass.",
        },
        {
          stepNumber: "4",
          title: "Release the feature",
          description: "Release the feature to the preview or production environment.",
        },
        {
          stepNumber: "5",
          title: "Generate test report",
          description: "Generate a full quality report for the preview URL.",
        },
      ],
    },
    [HeroWorkflowPersonaTabEnum.VibeCoders]: {
      label: "Vibe Coders",
      description: "Prompt, validate in cloud, fix quickly, then publish with confidence.",
      steps: [
        {
          stepNumber: "1",
          title: "Prompt for a feature",
          description: "Generate initial implementation from intent.",
        },
        {
          stepNumber: "2",
          title: "Muggle Test it",
          description: "Run Muggle Test to generate a full quality report.",
          highlight: true,
        },
        {
          stepNumber: "3",
          title: "Fix issues util works",
          description: "Keep fixing issues from feedbacks until checks pass.",
        },
        {
          stepNumber: "4",
          title: "Publish the feature",
          description: "Deploy the feature to the preview or production environment.",
        },
      ],
    },
    [HeroWorkflowPersonaTabEnum.ProductTeam]: {
      label: "Product Team",
      description: "Import journeys, generate reports, triage fixes, then retest with confidence.",
      steps: [
        {
          stepNumber: "1",
          title: "Import Designs, Specs, Tests, and Preview URL",
          description: "Import designs, specs, tests, and preview URL that must pass before release.",
        },
        {
          stepNumber: "2",
          title: "Muggle Test it",
          description: "Run Muggle Test to generate a full quality report.",
          highlight: true,
        },
        {
          stepNumber: "3",
          title: "Review and triage issues",
          description: "Review findings and prioritize fixes by user impact.",
        },
        {
          stepNumber: "4",
          title: "Publish the product",
          description: "Release the product to the production environment.",
        },
      ],
    },
  },
};

/**
 * Ladder tier 2: outcomes ordered by painfulness—journey coverage, fix priority, shareable proof.
 */
export const WHY_CHOOSE_LADDER_OUTCOME_CARDS: WhyChooseLadderOutcomeCard[] = [
  {
    pillarLabel: "Critical journeys coverage",
    icon: FileText,
    title: "Journeys that matter",
    description:
      "AI surfaces the paths that actually matter—signup, checkout, password reset, and the flows your release depends on.",
  },
  {
    pillarLabel: "Prioritized fixes",
    icon: ClipboardCheck,
    title: "Fix what hurts users first",
    description:
      "Issues are ordered by severity so your next commit targets real risk—not the loudest bug in the channel.",
  },
  {
    pillarLabel: "Visual evidence",
    icon: Camera,
    title: "Ticket-ready evidence",
    description:
      "Findings ship with screenshots, repro steps, and the exact error—drop straight into Jira, Linear, or Slack.",
  },
];

export const HOW_IT_WORKS_STEPS: HowItWorksStep[] = [
  {
    number: "1",
    icon: Globe,
    title: "Enter a URL",
    description: "Paste your staging or preview link. Vercel, Netlify, Cloudflare — any accessible URL works.",
    image: urlInputImage,
  },
  {
    number: "2",
    icon: Sparkles,
    title: "Generate and Pick User Journeys",
    description: "AI suggests critical user journeys. You choose what matters.",
    image: scenarioImage,
  },
  {
    number: "3",
    icon: PlayCircle,
    title: "Run and get results",
    description: "Screenshots, evidence, and what to fix first. Results in minutes.",
    image: resultsDashboardImage,
  },
];

export const PREVIEW_PROVIDERS: PreviewProvider[] = [
  {
    label: "Vercel Preview",
    href: "https://vercel.com/docs/deployments/generated-urls",
    viewBox: "0 0 76 65",
    svgPath: "M37.5274 0L75.0548 65H0L37.5274 0Z",
  },
  {
    label: "Netlify Deploy",
    href: "https://docs.netlify.com/deploy/deploy-types/deploy-previews/",
    viewBox: "0 0 256 256",
    svgPath:
      "M185.498 129.13l-8.695-8.695 21.976-21.976-13.11-13.11-30.67 30.67 8.695 8.695-8.695 8.695-13.11-13.11 30.67-30.67-13.11-13.11-30.67 30.67 13.11 13.11-8.695 8.695-30.67-30.67-21.976 21.976 8.695 8.695-8.695 8.695 21.976 21.976 13.11-13.11-30.67-30.67 8.695-8.695 30.67 30.67 13.11-13.11-21.976-21.976z",
  },
  {
    label: "Cloudflare Pages",
    href: "https://developers.cloudflare.com/pages/configuration/preview-deployments/",
    viewBox: "0 0 256 256",
    svgPath:
      "M195.9 137.5c1.2-4.2 1.8-8.6 1.8-13.2 0-24.5-19.8-44.3-44.3-44.3-9.3 0-17.9 2.9-25 7.8-8.7-20.6-29-35-52.6-35-31.5 0-57 25.5-57 57 0 5 .7 9.8 1.9 14.5C8.6 129.7 0 142.7 0 157.7c0 19.5 15.8 35.3 35.3 35.3h135.4c27.1 0 49-22 49-49 0-24.5-18-44.8-41.3-48.5h17.5z",
  },
];

export const EXAMPLES: ExampleCard[] = [
  {
    title: "Strengthen growth and conversion funnels",
    description:
      "Exercise signup, trial, and upgrade paths continuously so landing-page tests and funnel tweaks do not introduce silent drop-offs.",
    image: resultsDashboardImage,
    tag: "Growth",
    icon: ExampleUseCaseIconEnum.GROWTH,
  },
  {
    title: "Release faster without breaking core journeys",
    description:
      "Regression-test the flows customers rely on—checkout, onboarding, settings—so every ship keeps must-work paths intact.",
    image: scenarioImage,
    tag: "Velocity",
    icon: ExampleUseCaseIconEnum.RELEASE,
  },
  {
    title: "Diagnose where journeys stall",
    description:
      "Uncover experience gaps with evidence: carts that never reach checkout, forms that never submit, and navigation that leads nowhere.",
    image: evidencePreviewImage,
    tag: "Product quality",
    icon: ExampleUseCaseIconEnum.DIAGNOSE,
  },
];

const PRICING_DETAILS_SHARED =
  "Scan discovers use cases; run executes them. Token usage scales with use case depth and run frequency.";

export const PRICING_PLANS: PricingPlan[] = [
  {
    name: "Free",
    price: "$0",
    period: "/mo",
    description: "Try the workflow end to end and validate evidence quality.",
    features: ["3 concurrent workflows", "1M free tokens (one-time grant at signup)"],
    cta: "Start free",
    ctaAction: PricingPlanCtaActionEnum.SIGNUP,
    priceDisplay: PricingPlanPriceDisplayEnum.PRICE,
    popular: false,
    detailsTooltip: [
      "1M free tokens granted once at signup (never refreshed); top up anytime. 3 concurrent workflows (scans or runs at once).",
      PRICING_DETAILS_SHARED,
    ].join("\n\n"),
    trustLine: "No credit card required",
    tierId: SubscriptionPlanEnum.FREE,
  },
  {
    name: "Starter",
    price: "Free",
    period: "after you verify your email",
    description: "For vibe coders and individuals who want the full product experience.",
    features: ["10 concurrent workflows", "3M tokens / month (refilled on renewal)"],
    cta: "Sign up free",
    ctaAction: PricingPlanCtaActionEnum.SIGNUP,
    priceDisplay: PricingPlanPriceDisplayEnum.PRICE,
    popular: false,
    detailsTooltip: [
      "Starter costs nothing — verify your email after signing up and the account moves onto it automatically.",
      "3M tokens / month, refilled each month (never accumulates); top up anytime. Up to 10 concurrent workflows (scans or runs at once).",
      PRICING_DETAILS_SHARED,
    ].join("\n\n"),
    trustLine: "No credit card required",
    tierId: SubscriptionPlanEnum.STARTER,
  },
  {
    name: "Pro",
    price: "$199",
    period: "/mo",
    description: "For professionals and small teams that want meaningful coverage without maintenance overhead.",
    features: ["20 concurrent workflows", "20M tokens / month (refilled on renewal)"],
    cta: "Get Pro",
    ctaAction: PricingPlanCtaActionEnum.SIGNUP,
    priceDisplay: PricingPlanPriceDisplayEnum.PRICE,
    popular: true,
    badgeLabel: "Recommended",
    detailsTooltip: [
      "20M tokens / month, refilled on subscription renewal (never accumulates); top up anytime. Up to 20 concurrent workflows (scans or runs at once).",
      PRICING_DETAILS_SHARED,
    ].join("\n\n"),
    trustLine: "Cancel anytime · No long-term contract",
    tierId: SubscriptionPlanEnum.PROFESSIONAL,
  },
  {
    name: "Enterprise",
    price: "",
    period: "",
    description: "For teams that need more capacity, higher concurrency, and organization controls.",
    features: [
      "20+ concurrent workflows",
      "$60/seat/month (minimum 4 seats)",
      "Pay-as-you-go",
      "Admin, sub-accounts, and shared projects",
    ],
    cta: "Contact sales",
    ctaAction: PricingPlanCtaActionEnum.CONTACT_SALES,
    priceDisplay: PricingPlanPriceDisplayEnum.CONTACT_SALES,
    popular: false,
    detailsTooltip: [
      "Pay per use. 20+ concurrent workflows, plus admin, sub-accounts, and shared projects.",
      PRICING_DETAILS_SHARED,
    ].join("\n\n"),
  },
];

/** Footnotes displayed below the pricing grid; plan-specific detail lives in each card tooltip. */
export const PRICING_FOOTNOTES: readonly string[] = [
  "Every plan includes unlimited use cases and test cases per project; usage is metered in tokens.",
  "Free plans include a one-time signup grant. Paid plans include monthly token allocations refilled on renewal. Top-up tokens never expire.",
  "Token usage varies by site complexity. Simple pages cost less, complex web apps cost more.",
];

export const FAQ_ITEMS: FaqItem[] = [
  {
    id: "item-1",
    question: "How do token credits work?",
    answer:
      "Free plan users receive a one-time 1M token grant at signup — these tokens are never refreshed, so when they run out the only way forward is to upgrade. Paid plans (Starter: 3M, Pro: 20M) receive a fresh monthly grant on each subscription renewal — the grant balance is topped up to the plan amount, never reduced. Enterprise is pay-per-use at $5 per 1M tokens. Tokens you buy as a top-up package never expire and survive renewals, plan changes, and downgrades.",
  },
  {
    id: "item-2",
    question: "Is my data secure?",
    answer:
      "Your test data is encrypted in transit and at rest. Credentials for authenticated testing are stored in an isolated secrets vault and never logged. We do not access your source code — testing happens through the browser, the same way a real user would interact with your app.",
  },
  {
    id: "item-3",
    question: "How reliable is AI testing?",
    answer:
      "AI agents use human-visible labels (button text, form labels, headings) instead of CSS selectors. This means tests do not break when your UI changes — they adapt automatically. The result is more stable coverage than traditional selector-based E2E acceptance test automation that depends on fragile selectors.",
  },
  {
    id: "item-4",
    question: "How is Muggle Test different from Cypress or Playwright?",
    answer:
      "Cypress and Playwright require you to write and maintain test code — selectors break when the UI changes, and you need dedicated E2E acceptance testing expertise. Muggle Test uses AI agents that explore your app in a real browser, discover user journeys automatically, and adapt to changes. No code, no selectors, no maintenance.",
  },
  {
    id: "item-5",
    question: "Can I integrate this into my CI/CD pipeline?",
    answer:
      "Yes. Muggle Test integrates with your deployment pipeline through our API and MCP tools. Trigger scans automatically on every deploy or PR preview URL.",
  },
  {
    id: "item-6",
    question: "What happens when I run out of tokens?",
    answer:
      "Running workflows will fail when you are out of tokens — in-flight runs stop, and new runs cannot start until you add credits. Your projects and saved reports stay available. You can buy a top-up anytime from your dashboard (no need to wait for the next billing cycle). We highly recommend turning on auto top-up so CI and scheduled runs do not fail unexpectedly.",
  },
  {
    id: "item-7",
    question: "Why is this cost-effective?",
    answer:
      "A junior engineer focused on manual E2E acceptance testing often costs $4,000-6,000/month. Muggle Test Pro costs $199/month and covers ~4 full test runs. You get consistent, automated coverage without hiring, training, or managing extra testing headcount.",
  },
  {
    id: "item-8",
    question: "How fast can I see value?",
    answer:
      "Paste a URL, and you will have your first test report in under 10 minutes. No setup, no configuration, no code to write. Most teams run their first scan within 60 seconds of signing up.",
  },
  {
    id: "item-9",
    question: "Not live yet — do I need a domain?",
    answer:
      "No. You only need an accessible preview URL. Vercel, Netlify, and Cloudflare preview links all work. Password-protected URLs are supported too.",
  },
  {
    id: "item-10",
    question: "Can it test logged-in flows?",
    answer:
      "Yes. Add credentials in your project settings and Muggle Test handles authenticated flows — login, account management, checkout with saved payment methods. Credentials are encrypted and never stored in logs.",
  },
  {
    id: "item-11",
    question: "What if my app changes — do I need to redo everything?",
    answer:
      "No. AI agents discover journeys dynamically on each scan. When your app changes, the next scan adapts automatically. No test maintenance, no broken selectors, no re-recording.",
  },
  {
    id: "item-12",
    question: "Do I need to write code?",
    answer:
      "No. AI discovers the journeys automatically. You pick which ones to test and click run. If you want programmatic control, we also offer API and MCP integration for CI/CD workflows.",
  },
];

/** Closing CTA section copy — displayed between FAQ and footer. */
export const CLOSING_CTA: ClosingCta = {
  headline: "Ready to verify everything you create?",
  subheadline: "Paste a URL. Get your first test report in under 5 minutes.",
  primaryCta: "Create your first project now",
  trustLine: "No credit card required · Cancel anytime · Works with any URL",
};

/** Generate the JSON-LD @graph schema for the landing page. FAQPage is derived from FAQ_ITEMS to prevent drift. */
export function generateSchemaMarkup(): SchemaMarkup {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://www.muggle-ai.com/#organization",
        name: "Muggle AI",
        url: "https://www.muggle-ai.com",
        logo: {
          "@type": "ImageObject",
          url: "https://www.muggle-ai.com/muggleTestV0/public/images/muggleai-logo.png",
          width: 512,
          height: 512,
        },
        description: "An acceptance test agent that keeps your AI agent team building to your taste.",
        contactPoint: {
          "@type": "ContactPoint",
          email: "support@muggle-ai.com",
          contactType: "customer support",
          availableLanguage: ["English"],
        },
      },
      {
        "@type": "WebSite",
        "@id": "https://www.muggle-ai.com/#website",
        name: "Muggle AI",
        url: "https://www.muggle-ai.com",
      },
      {
        "@type": "WebPage",
        "@id": "https://www.muggle-ai.com/muggletestv0/landing#webpage",
        name: "Muggle Test — An acceptance test agent that keeps your AI agent team building to your taste",
        description:
          "An acceptance test agent that keeps your AI agent team building to your taste. Paste a preview URL and get evidence and what to fix first, in minutes.",
        url: "https://www.muggle-ai.com/muggletestv0/landing",
        inLanguage: "en-US",
        isPartOf: { "@id": "https://www.muggle-ai.com/#website" },
        about: { "@id": "https://www.muggle-ai.com/muggletestv0/landing#software" },
        publisher: { "@id": "https://www.muggle-ai.com/#organization" },
      },
      {
        "@type": "SoftwareApplication",
        "@id": "https://www.muggle-ai.com/muggletestv0/landing#software",
        name: "Muggle Test",
        description:
          "An acceptance test agent that keeps your AI agent team building to your taste. Paste a preview URL, pick user journeys, and get evidence-based test results in minutes.",
        url: "https://www.muggle-ai.com/muggletestv0/landing",
        applicationCategory: "DeveloperApplication",
        operatingSystem: "Web (any modern browser)",
        offers: [
          {
            "@type": "Offer",
            name: "Free",
            price: 0,
            priceCurrency: "USD",
            description: "Try the full workflow — paste a URL, get a report, see if Muggle Test fits your team.",
            url: "https://www.muggle-ai.com/muggletestv0/landing#pricing",
          },
          {
            "@type": "Offer",
            name: "Pro",
            price: 199,
            priceCurrency: "USD",
            description:
              "Teams shipping weekly that need consistent test coverage without maintaining test code or growing a manual testing team.",
            url: "https://www.muggle-ai.com/muggletestv0/landing#pricing",
          },
        ],
        publisher: { "@id": "https://www.muggle-ai.com/#organization" },
      },
      {
        "@type": "FAQPage",
        "@id": "https://www.muggle-ai.com/muggletestv0/landing#faq",
        mainEntity: FAQ_ITEMS.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      },
    ],
  };
}

export const FOOTER_LINKS: FooterLink[] = [
  { label: "Docs", href: "/muggleTestV0/docs" },
  { label: "Terms & Privacy", href: "/muggleTestV0/terms" },
  { label: "Contact", href: "mailto:support@muggle-ai.com", isExternal: true },
  { label: "Cookie Settings", href: "#cookie-settings" },
];
